import { Check } from 'lucide-react'
import { motion } from 'framer-motion'

import { BuyCreditsButton } from './BuyCreditsButton'
import { CREDIT_PACKAGES, pricePerCredit, savingPercent } from '@/features/billing/plans'
import {
  useRazorpayCheckout,
  RazorpayMockModal,
  CheckoutStatusToast,
} from '@/features/billing/useRazorpayCheckout'
import { cn } from '@/lib/utils/cn'

/**
 * The credit packs.
 *
 * Shown on the pricing page and again on the billing page.
 * Seamlessly integrates with Razorpay Checkout for instant top-up.
 */
export function PlanCards({ className }: { className?: string }) {
  const {
    initiateCheckout,
    loading,
    error,
    success,
    mockOrder,
    handleSimulateMockPayment,
    dismissModal,
  } = useRazorpayCheckout()

  return (
    <>
      <div className={cn('grid gap-4 md:grid-cols-3', className)}>
        {CREDIT_PACKAGES.map((pack) => {
          const saving = savingPercent(pack)
          return (
            <motion.section
              key={pack.id}
              data-testid={`plan-${pack.id}`}
              whileHover={{ y: -6, scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className={cn(
                'relative flex flex-col rounded-xl border bg-surface p-5 shadow-card transition-shadow hover:shadow-pop hover:border-primary/50',
                pack.highlight ? 'border-primary/40' : 'border-line',
              )}
            >
              {pack.highlight ? (
                <span
                  className="absolute -top-2.5 left-5 rounded-full border border-primary/40 bg-raised
                    px-2.5 py-0.5 text-micro font-medium uppercase tracking-wide text-primary"
                >
                  {pack.highlight}
                </span>
              ) : null}

              <h3 className="text-sm font-semibold text-ink">{pack.name}</h3>

              <p className="mt-3 flex items-baseline gap-1.5">
                <span className="text-2xl font-semibold tabular-nums text-ink">₹{pack.price}</span>
                <span className="text-sm text-ink-muted">one-off</span>
              </p>

              <p className="mt-1 text-sm text-ink-dim">
                <span className="font-medium tabular-nums text-ink">{pack.credits}</span> credits
                {saving > 0 ? (
                  <span className="text-ok"> · {saving}% cheaper per credit</span>
                ) : null}
              </p>
              <p className="mt-0.5 text-xs text-ink-muted">
                Works out at ₹{pricePerCredit(pack)} a credit.
              </p>

              <p className="mt-3 flex-1 text-sm text-ink-muted">{pack.summary}</p>

              <ul className="mt-4 space-y-1.5 text-xs text-ink-muted">
                {['Credits never expire', 'Only successful calls are charged', 'Full usage history'].map(
                  (line) => (
                    <li key={line} className="flex items-start gap-1.5">
                      <Check aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok" />
                      {line}
                    </li>
                  ),
                )}
              </ul>

              <div className="mt-5">
                <BuyCreditsButton
                  pack={pack}
                  onBuy={initiateCheckout}
                  loading={loading}
                />
              </div>
            </motion.section>
          )
        })}
      </div>

      {mockOrder ? (
        <RazorpayMockModal
          order={mockOrder}
          loading={loading}
          onSimulate={handleSimulateMockPayment}
          onClose={dismissModal}
        />
      ) : null}

      <CheckoutStatusToast error={error} success={success} />
    </>
  )
}
